/**
 * Batched passkey approval (docs/e2e.md "Fewer passkey ceremonies"): one WebAuthn assertion over
 * `H('viko/approval-batch/v1', {v: 1, items})`, where every item is the exact single-statement
 * challenge `{domain, digest = hashCanonical(domain, statement)}` a verifier would otherwise
 * require. A verifier accepts it only for its own item, so sharing the gesture never widens what
 * any single statement authorizes. Only enrollment, key-directory and step-up are batchable.
 */
import { fromBase64Url, toBase64Url } from './bytes.js';
import { DOMAIN, hashCanonical } from './hash.js';
export const APPROVAL_BATCH_DOMAIN = 'viko/approval-batch/v1';
export const KEY_DIRECTORY_DOMAIN = 'viko/key-directory/v1';
export const BATCHABLE_DOMAINS = [
    DOMAIN.enrollment,
    KEY_DIRECTORY_DOMAIN,
    DOMAIN.stepUp,
];
export const MAX_BATCH_ITEMS = 32;
/**
 * Single-ceremony sign-in (docs/e2e.md "Phone with one Face ID"): the hub's login nonce item is
 * batchable **only** as the exact pair `{login, device-endorsement}`. Neither domain may appear in
 * any other batch, so an endorsement can never ride along with enrollment, directory or step-up.
 */
export const LOGIN_DOMAIN = 'viko/login/v1';
export const LOGIN_PAIR_DOMAINS = [LOGIN_DOMAIN, DOMAIN.deviceEndorsement];
/**
 * Unlock via your Mac (docs/e2e.md "Unlock via a machine"): a returning browser's sign-in may add
 * ONE unlock-request item to that pair, so the same tap that signs in also asks a key-holding
 * daemon for the content keys. The item is batchable only in that exact triple.
 */
export const UNLOCK_REQUEST_BATCH_DOMAIN = 'viko/unlock-request/v1';
/** The item for one statement, from the challenge its verifier computes. */
export function approvalBatchItem(domain, challenge) {
    return { domain, digest: toBase64Url(challenge) };
}
/** Throws unless `items` is a well-formed batch (2–32 unique items, allowlisted domains). */
export function validateApprovalBatch(items) {
    if (!Array.isArray(items) || items.length < 2 || items.length > MAX_BATCH_ITEMS)
        throw new Error('invalid approval batch');
    const seen = new Set();
    for (const item of items) {
        if (typeof item !== 'object' ||
            item === null ||
            Object.keys(item).sort().join(',') !== 'digest,domain')
            throw new Error('invalid approval batch item');
        const { domain, digest } = item;
        if (typeof domain !== 'string' ||
            !(BATCHABLE_DOMAINS.includes(domain) ||
                LOGIN_PAIR_DOMAINS.includes(domain) ||
                domain === UNLOCK_REQUEST_BATCH_DOMAIN))
            throw new Error('domain is not batchable');
        let bytes;
        try {
            bytes = typeof digest === 'string' ? fromBase64Url(digest) : new Uint8Array();
        }
        catch {
            throw new Error('invalid approval batch digest');
        }
        if (bytes.length !== 32 || toBase64Url(bytes) !== digest)
            throw new Error('invalid approval batch digest');
        if (seen.has(digest))
            throw new Error('duplicate approval batch item');
        seen.add(digest);
    }
    const list = items;
    const pair = list.filter((i) => LOGIN_PAIR_DOMAINS.includes(i.domain));
    const unlock = list.filter((i) => i.domain === UNLOCK_REQUEST_BATCH_DOMAIN);
    if (unlock.length && (unlock.length !== 1 || pair.length !== 2))
        throw new Error('an unlock request is batchable only with sign-in');
    if (pair.length &&
        (items.length !== 2 + unlock.length || pair.length !== 2 || pair[0].domain === pair[1].domain))
        throw new Error('login and device endorsement are batchable only as one pair');
}
/** The login item for a hub-issued (HMAC, single-use) login challenge string. */
export async function loginBatchItem(challenge) {
    if (typeof challenge !== 'string' || !challenge || challenge.length > 256)
        throw new Error('invalid login challenge');
    return approvalBatchItem(LOGIN_DOMAIN, await hashCanonical(LOGIN_DOMAIN, { v: 1, challenge }));
}
/** The WebAuthn challenge for a batch. Validates the batch first. */
export async function createApprovalBatchChallenge(items) {
    validateApprovalBatch(items);
    return hashCanonical(APPROVAL_BATCH_DOMAIN, {
        v: 1,
        items: items.map((i) => ({ domain: i.domain, digest: i.digest })),
    });
}
/**
 * The challenge an assertion must cover for the statement whose own challenge is `challenge`:
 * the statement challenge itself (no batch) or the batch challenge when `batch` lists exactly
 * this `{domain, digest}` once. Throws for a malformed batch or one that omits the statement.
 */
export async function approvalChallengeFor(domain, challenge, batch) {
    if (batch === undefined)
        return new Uint8Array(challenge);
    validateApprovalBatch(batch);
    const digest = toBase64Url(challenge);
    if (batch.filter((i) => i.domain === domain && i.digest === digest).length !== 1)
        throw new Error('statement is not in the approval batch');
    return createApprovalBatchChallenge(batch);
}
//# sourceMappingURL=approval-batch.js.map